import {Controller} from "stimulus";
import debounce from "lodash/debounce";

export default class extends Controller<HTMLElement> {
  declare eventValue: keyof HTMLElementEventMap;
  declare delayValue: number;

  static values = {
    event: {type: String, default: "change"},
    delay: {type: Number, default: 500},
  };

  connect() {
    const debounceFn = debounce((event) => {
      this.element.dispatchEvent(
        new CustomEvent(`debounced:${this.eventValue}`, {
          bubbles: true,
          detail: event.detail,
        }),
      );
    }, this.delayValue);

    this.element.addEventListener(this.eventValue, debounceFn);

    // If the debounce event looks like it's for key input into a form field, then
    // should the form be submitted, we ought to cancel any debounced event. Without
    // this, the commonplace "submit form after typing in something" debounce model
    // will submit twice if the user e.g. hits Return - which triggers that first
    // "submit" event on the form - but then the timer fires for the debounce event
    // of whatever character the typed just before Return, and resubmits the form.
    // The web server gets loaded unnecessarily with two closely-spaced fetches and
    // the user has to wait a bit longer for the new page to fetch.
    //
    if (this.eventTypeIsKeyInputRelated(this.eventValue) && this.eventTargetIsPartOfAForm(this.element)) {
      this.element.form.addEventListener("submit", (_event) => {
        debounceFn.cancel();
      });
    }
  }

  private eventTypeIsKeyInputRelated(eventName: string) {
    return ["input", "keydown", "keyup", "keypress"].includes(eventName.toLowerCase());
  }

  private eventTargetIsPartOfAForm(element: HTMLElement): element is HTMLElement & {form: HTMLFormElement} {
    return "form" in element && !!(element as HTMLInputElement).form;
  }
}
